import { Controller } from "@hotwired/stimulus"
import { useClickOutside } from "../helpers/click_outside_mixin"

const DEBOUNCE_MS = 300

// Searchable client combobox with lazy JSON options for psychologist testing section.
export default class extends Controller {
  static targets = [ "input", "hidden", "listbox", "menu" ]
  static values = {
    searchUrl: String,
    baseUrl: String,
    autoNavigate: { type: Boolean, default: false },
    selectedId: String,
    selectedLabel: String,
    loadingLabel: String,
    emptyLabel: String
  }

  connect() {
    this._debounceTimer = null
    this._activeIndex = -1
    this._options = []
    this._fetchController = null

    if (this.hasSelectedIdValue && this.selectedIdValue) {
      this.hiddenTarget.value = this.selectedIdValue
    }
    if (this.hasSelectedLabelValue && this.selectedLabelValue) {
      this.inputTarget.value = this.selectedLabelValue
    }

    this._clickOutside = useClickOutside(this.element, () => this.closeMenu())
  }

  disconnect() {
    this._clickOutside?.remove()
    clearTimeout(this._debounceTimer)
    this._fetchController?.abort()
  }

  onFocus() {
    if (this._options.length === 0) {
      this.fetchOptions(this.inputTarget.value)
    } else {
      this.openMenu()
    }
  }

  search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => {
      this.fetchOptions(this.inputTarget.value)
    }, DEBOUNCE_MS)
  }

  async fetchOptions(query) {
    this._fetchController?.abort()
    this._fetchController = new AbortController()

    const url = new URL(this.searchUrlValue, window.location.origin)
    const normalized = query.trim()
    if (normalized) url.searchParams.set("q", normalized)

    this.setLoading(true)
    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this._fetchController.signal
      })
      if (!response.ok) return

      const data = await response.json()
      this._options = Array.isArray(data.clients) ? data.clients : []
      this._activeIndex = -1
      this.renderOptions()
      this.openMenu()
    } catch (error) {
      if (error.name === "AbortError") return
      this._options = []
      this.renderOptions()
      this.openMenu()
    } finally {
      this.setLoading(false)
    }
  }

  renderOptions() {
    this.listboxTarget.replaceChildren()

    if (this._options.length === 0) {
      const empty = document.createElement("p")
      empty.className = "psychologist-client-search__empty"
      empty.textContent = this.emptyLabelValue
      this.listboxTarget.appendChild(empty)
      return
    }

    this._options.forEach((client, index) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "psychologist-client-search__option"
      button.role = "option"
      button.id = `${this.listboxTarget.id}-option-${index}`
      button.dataset.clientId = String(client.id)
      button.dataset.clientLabel = client.label
      button.dataset.action = "click->psychologist-client-picker#selectOption"
      button.setAttribute("aria-selected", "false")

      const label = document.createElement("span")
      label.className = "psychologist-client-search__option-label"
      label.textContent = client.label
      button.appendChild(label)

      if (client.meta) {
        const meta = document.createElement("span")
        meta.className = "psychologist-client-search__option-meta"
        meta.textContent = client.meta
        button.appendChild(meta)
      }

      this.listboxTarget.appendChild(button)
    })
  }

  selectOption(event) {
    const button = event.currentTarget
    this.applySelection(button.dataset.clientId, button.dataset.clientLabel)
  }

  applySelection(clientId, label) {
    this.hiddenTarget.value = clientId
    this.inputTarget.value = label
    this.closeMenu()

    if (this.autoNavigateValue) {
      this.navigateToClient(clientId)
    }
  }

  navigateToClient(clientId) {
    const url = new URL(this.baseUrlValue, window.location.origin)
    url.searchParams.set("client_id", clientId)
    window.location.assign(url.toString())
  }

  submitForm(event) {
    if (!this.hiddenTarget.value) {
      event.preventDefault()
      this.inputTarget.focus()
      this.fetchOptions(this.inputTarget.value)
    }
  }

  onKeydown(event) {
    if (!this.menuTarget.hidden && this._options.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        this.moveActive(1)
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        this.moveActive(-1)
        return
      }
      if (event.key === "Enter") {
        const option = this.optionButtons()[this._activeIndex]
        if (option) {
          event.preventDefault()
          this.applySelection(option.dataset.clientId, option.dataset.clientLabel)
        }
        return
      }
    }

    if (event.key === "Escape") {
      this.closeMenu()
    }
  }

  moveActive(delta) {
    const buttons = this.optionButtons()
    if (buttons.length === 0) return

    this._activeIndex = Math.max(0, Math.min(buttons.length - 1, this._activeIndex + delta))
    buttons.forEach((button, index) => {
      const active = index === this._activeIndex
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-selected", active ? "true" : "false")
      if (active) button.scrollIntoView({ block: "nearest" })
    })
  }

  optionButtons() {
    return Array.from(this.listboxTarget.querySelectorAll(".psychologist-client-search__option"))
  }

  openMenu() {
    this.menuTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    this._clickOutside.add()
  }

  closeMenu() {
    this.menuTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this._clickOutside.remove()
    this._activeIndex = -1
    this.optionButtons().forEach((button) => {
      button.classList.remove("is-active")
      button.setAttribute("aria-selected", "false")
    })
  }

  setLoading(loading) {
    this.element.classList.toggle("is-loading-options", loading)
    if (loading) {
      this.listboxTarget.replaceChildren()
      const status = document.createElement("p")
      status.className = "psychologist-client-search__status"
      status.setAttribute("role", "status")
      status.textContent = this.loadingLabelValue
      this.listboxTarget.appendChild(status)
      this.openMenu()
    }
  }
}
